import { Component, computed, input, signal } from '@angular/core';
import { IntegrationStatus } from '../../models/integration-status';
import { pillClasses, statusIcon, statusLabel, statusMeaning } from './integration-status.util';

let nextId = 0;

/**
 * A status pill: icon and label on a tint of the status colour, so it never relies on colour alone.
 * With help, a "?" beside it opens what that status means (Heinrich, 28/10/2026), which replaced the
 * legend at the foot of the drawer. It closes on a second click, Escape, or when focus leaves it.
 */
@Component({
  selector: 'app-integration-pill',
  template: `<span class="inline-flex items-center gap-1">
    <span class="inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold text-[var(--base-text-alt-dark)]"
          [ngClass]="classes()"><span class="material-icons text-[14px]! leading-none" aria-hidden="true">{{ icon() }}</span>{{ label() }}</span>
    <span *ngIf="help()" class="relative inline-flex" (focusout)="onFocusOut($event)">
      <button type="button" data-status-help
              class="flex h-5 w-5 items-center justify-center rounded-full border border-[var(--app-border)] bg-white text-[11px] font-semibold leading-none text-[var(--base-text-alt-dark)] hover:border-[var(--primary-dark)] hover:text-[var(--primary-dark)]"
              [attr.aria-expanded]="open()" [attr.aria-controls]="popoverId" [attr.aria-label]="'What ' + label() + ' means'"
              (click)="open.set(!open())" (keydown.escape)="close($event)">?</button>
      <span *ngIf="open()" [id]="popoverId" role="note" data-status-meaning
            class="absolute right-0 top-6 z-10 w-56 whitespace-normal rounded-lg border border-[var(--app-border)] bg-white p-2.5 text-xs font-normal leading-snug text-[var(--base-text-alt-dark)] shadow-md"><strong>{{ label() }}</strong>: {{ meaning() }}</span>
    </span>
  </span>`,
  standalone: false
})
export class IntegrationPillComponent {
  readonly status = input.required<IntegrationStatus>();
  /** Show the "?" with the status's meaning. */
  readonly help = input(false);
  /** The weather Check distance, for the meaning of Check. */
  readonly km = input<number | null | undefined>(20);
  /** A weather pill: its meanings speak of readings, and of ET0's extra day. */
  readonly weather = input(false);

  readonly label = computed(() => statusLabel(this.status()));
  readonly icon = computed(() => statusIcon(this.status()));
  readonly classes = computed(() => pillClasses(this.status()));
  readonly meaning = computed(() => statusMeaning(this.status(), this.km() ?? 20, this.weather()));
  readonly open = signal(false);
  readonly popoverId = `integration-status-meaning-${nextId++}`;

  /** Escape closes the explanation only, not the drawer behind it. */
  close(event: Event): void {
    if (this.open()) {
      event.stopPropagation();
      this.open.set(false);
    }
  }

  onFocusOut(event: FocusEvent): void {
    const holder = event.currentTarget as HTMLElement;
    if (!holder.contains(event.relatedTarget as Node | null)) {
      this.open.set(false);
    }
  }
}
